import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { orange } from "@mui/material/colors";
import {
  AutosaveEditTaskForm,
  TaskSyncFromDocumentPayload,
} from "components/Tasks/AutosaveEditTaskForm";
import { TaskFormType } from "components/Tasks/types";
import { DocumentWithMetadataQuery } from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";
import { v4 } from "uuid";

import type { DocumentFacility } from "../types";

type DocumentWithMetadataResult = DocumentWithMetadataQuery["document"];

export const RelatedTasksTabHeader = ({ document }: { document?: DocumentWithMetadataResult }) => {
  const theme = useTheme();

  const needsReview = useMemo(() => {
    return !!document?.documentMetadata?.needsReview;
  }, [document]);

  const tasks = useMemo(() => {
    return document?.tasks ?? [];
  }, [document?.tasks]);

  return (
    <Box display="flex" alignItems="center" gap={theme.spacing(1)}>
      {needsReview && tasks.length > 0 ? (
        <CircleIcon sx={{ color: orange[500], fontSize: "7px" }} />
      ) : null}
      Related Tasks
    </Box>
  );
};

type RelatedTasksSectionProps = {
  document?: DocumentWithMetadataResult;
  /** Live document form values from review dialog (same order as facilities picker). */
  documentFormFacilities?: DocumentFacility[];
  documentFormDueDate?: string | null;
};

export const RelatedTasksSection = ({
  document,
  documentFormFacilities,
  documentFormDueDate,
}: RelatedTasksSectionProps) => {
  const theme = useTheme();
  const [showAlert, setShowAlert] = useState(true);

  const handleCloseAlert = useCallback(() => {
    setShowAlert(false);
  }, []);

  const needsReview = useMemo(() => {
    return !!document?.documentMetadata?.needsReview;
  }, [document]);

  const tasks = useMemo(() => {
    return document?.tasks ?? [];
  }, [document?.tasks]);

  const syncFromDocument: TaskSyncFromDocumentPayload | undefined = useMemo(() => {
    if (documentFormFacilities === undefined) {
      return undefined;
    }
    return {
      facilityIds: documentFormFacilities.map((f) => f.id),
      dueDateIso: documentFormDueDate ?? null,
    };
  }, [documentFormDueDate, documentFormFacilities]);

  const syncTaskFromDocumentDetails = tasks.length > 0 && syncFromDocument !== undefined;

  if (tasks.length === 0) {
    return (
      <Grid size={{ xs: 12 }} height="100%">
        <Typography
          variant="body1"
          color="text.secondary"
          mt={theme.spacing(3)}
          textAlign="center"
          fontStyle="italic"
        >
          No tasks found for this document.
        </Typography>
      </Grid>
    );
  }

  return (
    <Stack sx={{ width: "100%", height: "100%" }} spacing={theme.spacing(2)}>
      {showAlert && needsReview && (
        <Alert
          action={
            <IconButton aria-label="close" color="inherit" size="small" onClick={handleCloseAlert}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          severity="info"
        >
          <AlertTitle>Permit renewal task added automatically</AlertTitle>
          This permit renewal has been added to Tasks. Please review the details below.
        </Alert>
      )}

      {tasks.map((task) => (
        <AutosaveEditTaskForm
          key={task.id}
          isLoading={false}
          showViewInTasks={true}
          initialValues={taskToTaskFormType(task)}
          syncTaskFromDocumentDetails={syncTaskFromDocumentDetails}
          syncFromDocument={syncFromDocument}
        />
      ))}
    </Stack>
  );
};

const taskToTaskFormType = (
  task: NonNullable<DocumentWithMetadataResult["tasks"]>[number]
): TaskFormType => {
  return {
    taskId: task.id,
    seriesId: task.seriesId,
    tenantId: task.tenantId,
    title: task.title ?? "",
    assignee: task.assignee ?? null,
    description: task.description ?? "",
    frequency: task.frequency,
    interval: task.interval,
    taskStatus: task.status,
    reminderDays:
      task.reminderDays.map((reminder) => ({
        days: reminder,
        id: v4(),
      })) ?? [],
    deadline: task.startDate,
    tags: task.taskTags.map((taskTag) => taskTag.tag) ?? [],
    links:
      task.links?.map((link) => ({
        url: link,
        id: v4(),
      })) ?? [],
    subtasks:
      task.subtasks.map((subtask) => ({
        id: subtask.id,
        title: subtask.title,
        completed: false,
      })) ?? [],
    facilityIds: task.facilities.map((facility) => facility.id) ?? [],
    watchers: task.watchers ?? [],
    documents: task.documents ?? [],
    completedOn: task.completedAt,
    completionTiming: null,
    completionDeltaDays: null,
    isSuggested: false,
  };
};
